import React, { useCallback, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { GeneralComments, HOMESITE_NOTES_MAX_LENGTH, STANDARD_HEIGHT_OF_DROPDOWN } from "../../../../../../../../../utils/constants";
import { useDialogBoxContext } from "../../../../../../../../../components/decisionDialogBox/decisionDialogBoxContext";
import { useBrowserClosureWarning } from "../../../../../../../../../utils/functions";
import {
  decisionDialogBoxMessage,
  decisionDialogBoxTitle,
} from "../../../../../../../../../components/decisionDialogBox/decisionDialogBox";
import { thinDarkScrollbar } from "../../../../../../../../../utils/scrollbarStyles";

interface AddNoteFormProps {
  milestones: string[];
  selectedMilestone: string;
  setSelectedMilestone: (milestone: string) => void;
  noteText: string;
  setNoteText: (text: string) => void;
  handleSaveNote: () => void;
  handleBack: () => void;
  onNoteDirtyChange?: (dirty: boolean) => void;
  setChildHandlers?: (handlers: {
    save: () => Promise<void>;
    cancel: () => void;
  }) => void;
}

const AddNoteForm: React.FC<AddNoteFormProps> = ({
  milestones,
  selectedMilestone,
  setSelectedMilestone,
  noteText,
  setNoteText,
  handleSaveNote,
  handleBack,
  onNoteDirtyChange,
  setChildHandlers,
}) => {
  const handleNoteTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    if (text.length <= HOMESITE_NOTES_MAX_LENGTH) {
      setNoteText(text);
    }
  };

  const getValidMilestoneValue = (milestone: string): string => {
    if (!milestone || milestones.length === 0) {
      return milestones && milestones.length > 0 ? milestones[0] : "";
    }

    // Treat the aggregate filter label as no specific milestone (pick first real milestone)
    if (milestone === "All Milestones") {
      return milestones && milestones.length > 0 ? milestones[0] : "";
    }

    if (milestone === GeneralComments || milestones.includes(milestone)) {
      return milestone;
    }

    return GeneralComments;
  };

  // Ensure selectedMilestone is always valid
  const validSelectedMilestone = getValidMilestoneValue(selectedMilestone);

  useEffect(() => {
    const dirty = noteText.trim() !== "" && !!validSelectedMilestone;
    onNoteDirtyChange?.(dirty);
  }, [noteText, validSelectedMilestone, onNoteDirtyChange]);

  const { showDecisionDialogBox } = useDialogBoxContext();
  const isTryingToLeave = !!noteText.trim() && !!validSelectedMilestone;
  useBrowserClosureWarning(isTryingToLeave);
  const handleBackButtonCheck = () => {
    if (isTryingToLeave) {
      showDecisionDialogBox(
        true,
        decisionDialogBoxTitle,
        decisionDialogBoxMessage,
        () => {
          save();
          handleBack();
        },
        () => {
          setNoteText("");
          setSelectedMilestone(selectedMilestone);
          onNoteDirtyChange?.(false);
          handleBack();
        }
      );
    } else {
      setNoteText("");
      setSelectedMilestone(selectedMilestone);
      onNoteDirtyChange?.(false);
      handleBack();
    }
  };

  /* Notify parent component when note is dirty (i.e., has text and milestone) */
  useEffect(() => {
    const dirty = noteText.trim() !== "" && !!validSelectedMilestone;
    onNoteDirtyChange?.(dirty);
  }, [noteText, validSelectedMilestone, onNoteDirtyChange]);

  /* Save the current note */
  const save = useCallback(async () => {
    await handleSaveNote();
  }, [handleSaveNote]);

  /* Reset note state and notify parent component */
  const cancel = useCallback(() => {
    setNoteText("");
    setSelectedMilestone("All Milestones");
    onNoteDirtyChange?.(false);
  }, [setNoteText, setSelectedMilestone, onNoteDirtyChange]);

  /* Provide save and cancel handlers to parent component */
  useEffect(() => {
    setChildHandlers?.({
      save,
      cancel,
    });
  }, [setChildHandlers, save, cancel]);

  return (
    <Box>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <IconButton onClick={handleBackButtonCheck}>
          <ArrowBackIcon sx={{ color: "#1C2B36" }} />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "16px" }}>
          Add Note
        </Typography>
      </Box>

      <InputLabel
        id="milestone-select-label"
        sx={{
          fontWeight: "500",
          fontSize: "16px",
          color: "#3E5463",
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
        }}
        required
      >
        Select Milestone
      </InputLabel>
      <FormControl
        fullWidth
        variant="standard"
        style={{ marginBottom: "16px" }}
      >
        <Select
          IconComponent={(props) => (
            <GridExpandMoreIcon
              {...props}
              sx={{
                fontSize: 25,
              }}
            />
          )}
          labelId="milestone-select-label"
          value={validSelectedMilestone}
          onChange={(event) => setSelectedMilestone(event.target.value)}
          label="Select Milestone"
          sx={{
            "& .MuiSelect-icon": {
              color: "#3E5463",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: STANDARD_HEIGHT_OF_DROPDOWN,
                overflowY: "auto",
                ...thinDarkScrollbar,
              },
            },
          }}
        >
          {/* Add General Comments as a static option */}
          <MenuItem key="general-comments" value={GeneralComments}>
            {GeneralComments}
          </MenuItem>

          {/* Dynamically map through milestones */}
          {milestones.map((milestone) => (
            <MenuItem key={milestone} value={milestone}>
              {milestone}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <InputLabel
        sx={{
          fontWeight: "500",
          fontSize: "16px",
          color: "#3E5463",
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
        }}
        required
      >
        Add a Note
      </InputLabel>
      <TextField
        placeholder="Type note"
        multiline
        rows={4}
        value={noteText}
        onChange={handleNoteTextChange}
        fullWidth
        variant="standard"
        inputProps={{ maxLength: HOMESITE_NOTES_MAX_LENGTH, style: { color: "#1C2B36" } }}
        style={{ marginBottom: "16px" }}
      />

      <Typography
        variant="caption"
        color="textSecondary"
        style={{ display: "block", marginBottom: "16px" }}
      >
        {noteText.length}/{HOMESITE_NOTES_MAX_LENGTH} characters
      </Typography>

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="outlined"
          onClick={() => {
            setNoteText("");
            setSelectedMilestone(selectedMilestone);
            onNoteDirtyChange?.(false);
            handleBack();
          }}
          style={{ textTransform: "none", border: "2px solid #005DAA" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!noteText.trim() || !validSelectedMilestone}
          style={{ textTransform: "none" }}
          onClick={() => {
            handleSaveNote();
            onNoteDirtyChange?.(false);
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default AddNoteForm;
